import React, { useEffect, useRef, useState } from 'react';
import { FileText } from 'lucide-react';
import { profile } from '../data/profile';
import { useActiveSection } from '../hooks/useReveal';

const links = [
  { id: 'experience', label: 'Experience' },
  { id: 'projects', label: 'Projects' },
  { id: 'leadership', label: 'Leadership' },
  { id: 'skills', label: 'Skills' },
  { id: 'contact', label: 'Contact' },
] as const;

const sectionIds = links.map((l) => l.id);

const Nav: React.FC = () => {
  const active = useActiveSection(sectionIds);
  const [scrolled, setScrolled] = useState(false);
  const progress = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 8);
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (progress.current) progress.current.style.transform = `scaleX(${max > 0 ? window.scrollY / max : 0})`;
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 border-b transition-colors duration-300 ${
        scrolled ? 'border-ink-700/80 bg-ink/75 backdrop-blur-md' : 'border-transparent'
      }`}
    >
      <nav className="mx-auto flex h-16 max-w-page items-center justify-between px-5 sm:px-8">
        <a href="#top" className="group flex items-center gap-2.5" aria-label="Back to top">
          <span className="grid h-8 w-8 place-items-center rounded-md border border-ink-600 bg-ink-800 font-mono text-[11px] font-semibold text-accent transition-colors group-hover:border-accent/60">
            AG
          </span>
          <span className="hidden text-sm font-medium text-fg sm:inline">{profile.name}</span>
        </a>

        <ul className="hidden items-center gap-1 md:flex">
          {links.map((link) => (
            <li key={link.id}>
              <a
                href={`#${link.id}`}
                className={`relative rounded-md px-3 py-1.5 text-sm transition-colors ${
                  active === link.id ? 'text-fg' : 'text-fg-muted hover:text-fg'
                }`}
              >
                {link.label}
                {active === link.id && (
                  <span className="absolute inset-x-3 -bottom-[13px] h-px bg-accent" aria-hidden />
                )}
              </a>
            </li>
          ))}
        </ul>

        <a
          href={profile.resume}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-md border border-ink-600 bg-ink-800 px-3.5 py-1.5 text-sm text-fg transition-colors hover:border-accent/60 hover:text-accent"
        >
          <FileText size={14} />
          Résumé
        </a>
      </nav>
      <div
        ref={progress}
        className="absolute -bottom-px left-0 h-px w-full origin-left bg-gradient-to-r from-accent/0 via-accent/70 to-accent"
        style={{ transform: 'scaleX(0)' }}
        aria-hidden
      />
    </header>
  );
};

export default Nav;
